'use client';

import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Spinner (FE-004) — the single shared loading indicator.
 *
 * Replaces the ~284 ad-hoc `<Loader2 className="animate-spin" />` instances
 * scattered across the app with one accessible, consistently-sized primitive.
 *
 *   <Spinner />                      // inline, default size
 *   <Spinner size="lg" label="Saving…" />
 *   <Spinner center className="py-12" />   // centered block (page/list loading)
 */
const SIZES = { xs: 'h-3 w-3', sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8', xl: 'h-10 w-10' } as const;

export interface SpinnerProps {
  size?: keyof typeof SIZES;
  /** Centered block with vertical padding (for page/list-level loading). */
  center?: boolean;
  /** Visible label rendered next to the spinner; also used as the a11y label. */
  label?: string;
  className?: string;
}

export function Spinner({ size = 'md', center = false, label, className }: SpinnerProps) {
  const icon = (
    <Loader2
      className={cn(SIZES[size], 'animate-spin text-gray-400 dark:text-gray-500', !label && className)}
      aria-hidden
    />
  );

  if (!label && !center) return icon;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400',
        center && 'justify-center w-full py-12',
        label ? className : undefined,
      )}
    >
      {icon}
      {label ? <span>{label}</span> : <span className="sr-only">Loading…</span>}
    </div>
  );
}

export default Spinner;
